import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { RequestOriginGroup } from "../../../utils/models";
import { ORIGIN_GROUPS } from "../../../utils/requestOrigins";
import { type SystemChipItem, SystemChips } from "../../UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { type OriginFilterControl, RequestOriginFilterLayout } from "./requestOriginMeta";
import { useOriginFilterChange } from "./useOriginFilterChange";

type Props = { control: OriginFilterControl };

/** The Origin filter as a chip row for a phone filter sheet: none chosen means all, each chip carries its count. */
export const RequestOriginFilterChips: FC<Props> = ({ control }) => {
	const { t } = useTranslation();
	const onChange = useOriginFilterChange(control, RequestOriginFilterLayout.Gaps);
	const items = useMemo<SystemChipItem<RequestOriginGroup>[]>(
		() =>
			ORIGIN_GROUPS.map((group) => ({
				value: group,
				label: t("request.origin.filter.chip", { group: t(`request.origin.group.${group}`), count: control.counts[group] }),
			})),
		[t, control.counts],
	);
	const selectedItems = useMemo(() => items.filter((item) => control.selected.includes(item.value)), [items, control.selected]);
	return (
		<SystemChips
			items={items}
			selectedItems={selectedItems}
			onChange={(item, selected) => onChange(selected ? [...control.selected, item.value] : control.selected.filter((group) => group !== item.value))}
			touchTarget
			forceFitContent
			rootSx={{ justifyContent: "flex-start" }}
			dataTestId="request-origin-filter"
			getItemDataTestId={(item) => `request-origin-filter-${item.value}`}
		/>
	);
};
